import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { paymentsApi } from '@/api/paymentsApi';
import type { Coupon } from '@/api/paymentsApi';

function errorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error || fallback;
}

/**
 * Coupon entry for a checkout. The discount shown here is only ever a preview:
 * the server re-validates the code against the stored booking when the payment
 * is created, so a tampered value cannot buy a cheaper session.
 */
export function useCoupon({ amount, serviceSlug }: { amount: number; serviceSlug?: string }) {
  const { isAuthenticated } = useAuth();
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [validating, setValidating] = useState(false);
  const [available, setAvailable] = useState<Coupon[]>([]);
  const [appOnly, setAppOnly] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    paymentsApi
      .getCoupons(serviceSlug ? { service_slug: serviceSlug } : undefined)
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.coupons ?? res.data ?? []) as Coupon[];
        setAvailable(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAvailable([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, serviceSlug]);

  // Picking a coupon from the list passes its code in. Setting state and then
  // reading it back would validate the previous code, so take it as an argument.
  const apply = useCallback(async (next?: string) => {
    const trimmed = (next ?? code).trim().toUpperCase();
    if (!trimmed) return;
    if (trimmed !== code) setCode(trimmed);
    setValidating(true);
    try {
      const res = await paymentsApi.validateCoupon({
        code: trimmed,
        amount,
        service_slug: serviceSlug,
      });
      const body = res.data as
        | { valid?: boolean; discount?: number; message?: string; code?: string }
        | undefined;
      setAppOnly(body?.code === 'app_only');
      if (body?.valid) {
        setApplied(true);
        setDiscount(Number(body.discount) || 0);
        toast.success(body.message || 'Coupon applied');
        return;
      }
      setApplied(false);
      setDiscount(0);
      if (body?.code !== 'app_only') {
        toast.error(body?.message || 'That code is not valid for this session');
      }
    } catch (err) {
      setApplied(false);
      setDiscount(0);
      toast.error(errorMessage(err, 'Could not check that code'));
    } finally {
      setValidating(false);
    }
  }, [code, amount, serviceSlug]);

  const remove = useCallback(() => {
    setApplied(false);
    setCode('');
    setDiscount(0);
    setAppOnly(false);
  }, []);

  return {
    code,
    setCode,
    applied,
    discount: applied ? discount : 0,
    validating,
    available,
    /** The code exists but is reserved for the app; the UI offers the app. */
    appOnly,
    apply,
    remove,
    /** What to send to the payment endpoint, or undefined when nothing applies. */
    codeForPayment: applied ? code.trim() : undefined,
  };
}
